import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'

import { SelectProps } from '@forto-dev/phoenix-ui-core'

import Client from '@/api/client'
import { getConfig } from '@/app/config'

import {
  ContactV2PaginationResponse,
  ContactV2Response,
  LegalEntityV3PaginationResponse,
  LegalEntityV3Response,
} from '../types/MasterData'
import { formatContactLabel } from '../utils/formatUtils'

const basePath = '/api/customer-master-data'
const client = new Client(getConfig().API_ENDPOINT ?? '')

function getLegalEntities(
  role?: 'shipper' | 'consignee',
  query?: string,
  isBillableCustomer?: boolean,
  accountId?: string
) {
  const params: {
    search?: string
    role?: 'shipper' | 'consignee'
    isBillableCustomer?: boolean
    accountId?: string
  } = {
    search: query,
  }
  if (role) {
    params.role = role
  }
  if (isBillableCustomer) {
    params.isBillableCustomer = true
  }
  if (accountId) {
    params.accountId = accountId
  }
  return client.get<LegalEntityV3PaginationResponse>(`${basePath}/v3/legal-entities`, {
    params,
  })
}

function getContactsForLegalEntities(legalEntity?: string, query?: string) {
  return client.get<ContactV2PaginationResponse>(`${basePath}/contacts`, {
    params: { legalEntity: [legalEntity], search: query },
  })
}

export const getLegalEntityDetails = (legalEntityCode: string) => {
  return client.get<LegalEntityV3Response>(`${basePath}/v3/legal-entities/${legalEntityCode}`)
}

const getContactDetailsByID = (contactID: string) => {
  return client.get<ContactV2Response>(`${basePath}/contacts/${contactID}`)
}

export const useContactDetailsQuery = (contactID: string | null = '', enabled = true) => {
  return useQuery({
    queryKey: ['contact', contactID],
    queryFn: () => getContactDetailsByID(contactID as string),
    enabled: enabled && !isEmpty(contactID),
  })
}

export const useLegalEntityDetailsQuery = (legalEntityCode: string = '', enabled = true) => {
  return useQuery({
    queryKey: ['legalEntity', legalEntityCode],
    queryFn: () => getLegalEntityDetails(legalEntityCode),
    enabled: enabled && !isEmpty(legalEntityCode),
  })
}

export const useContactsQuery = (legalEntity?: string, query?: string) => {
  return useQuery({
    queryKey: ['contactsForLegalEntity', legalEntity, query],
    enabled: legalEntity != null,
    queryFn: () => getContactsForLegalEntities(legalEntity, query),
  })
}

export const useLegalEntitiesOptions = ({
  role,
  query,
  isBillableCustomer,
  accountId,
}: {
  role?: 'shipper' | 'consignee'
  query?: string
  isBillableCustomer?: boolean
  accountId?: string
}) => {
  return useQuery({
    queryKey: ['legalEntities', role, query, isBillableCustomer, accountId],
    queryFn: async () => {
      const result = await getLegalEntities(role, query, isBillableCustomer, accountId)
      const legalEntities = result.items ?? []
      const legalEntitiesAutocompleteItems: SelectProps['items'] = legalEntities.map((item) => ({
        value: item.code,
        label: item.name,
      }))

      return legalEntitiesAutocompleteItems
    },
  })
}

export const useContactsOptions = (legalEntityID?: string, query?: string) => {
  const { data, ...rest } = useContactsQuery(legalEntityID, query)

  const contacts = data?.items ?? []
  const contactsAutcompleteItems: SelectProps['items'] = contacts.map((item) => ({
    value: item.id,
    label: formatContactLabel(item) ?? '',
  }))

  return {
    data: contactsAutcompleteItems,
    ...rest,
  }
}
